/**
 * Display helpers. All of these accept the real API types: ISO date strings
 * (Prisma serialises Dates to strings) and prices as decimal strings.
 */

const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-indigo-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-fuchsia-500',
  'bg-teal-500',
];

/** Deterministic avatar background so a given user always looks the same. */
export function avatarColor(seed = '') {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function getInitials(name = '') {
  const cleaned = String(name).replace(/[^a-zA-Z0-9 ]/g, ' ').trim();
  if (!cleaned) return '?';
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function toDate(value) {
  // `new Date(null)` is the epoch, not an invalid date - a missing timestamp
  // must read as "no date" rather than as 1 Jan 1970.
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Compact age for a conversation row: now / 5m / 3h / Yesterday / 4d / 12 Sep. */
export function formatRelativeDate(value) {
  const date = toDate(value);
  if (!date) return '';

  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d`;

  return formatShortDate(date);
}

/** Clock time beside a message bubble, e.g. "14:32". */
export function formatClockTime(value) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Compact order date, e.g. "12 Sep". */
export function formatShortDate(value) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/** Month and year only, e.g. "Mar 2024" - how long an account has been a member. */
export function formatMonthYear(value) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString([], { month: 'short', year: 'numeric' });
}

/** Money, always two decimals. Accepts the API's decimal strings. */
export function formatPrice(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return `$${value.toFixed(2)}`;
}

/** Integer with thousands separators, for stat rows. */
export function formatCount(value, locale) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString(locale);
}

/** Truncation is done in CSS, but a hard cap keeps very long previews cheap. */
export function truncate(text = '', max = 120) {
  const value = String(text);
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
